import { useState, useMemo } from 'react';
import type { MissedBreaks } from '@sdk/automatedBreaks/types/MissedBreaks';
import { AutomatedBreakType } from '@sdk/settings/types';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import type { AttestationForRequest } from '@sdk/automatedBreaks/types/Attestation';

export const useBreakAttestationsModal = (missedBreaks: MissedBreaks | null) => {
  const [attestationOptions, setAttestationOptions] = useState<Record<string, AttestationOption>>(
    {}
  );
  const [attestationTexts, setAttestationTexts] = useState<Record<string, string>>({});
  const [isScrollLocked, setIsScrollLocked] = useState<boolean>(false);

  const missedBreaksArray = useMemo(() => {
    if (!missedBreaks) {
      return [];
    }

    return Object.entries(missedBreaks)
      .map(([automatedType, breakNumbers]) => {
        return breakNumbers.map((breakNumber) => ({
          type: automatedType as AutomatedBreakType,
          breakNumber,
          key: `${automatedType}/${breakNumber}`,
        }));
      })
      .reduce((flattened, array) => [...flattened, ...array], []);
  }, [missedBreaks]);

  const attestations = useMemo(() => {
    return missedBreaksArray.map(({ key, type, breakNumber }) => {
      return {
        breakType: type,
        breakNumber,
        option: attestationOptions[key],
        text: attestationTexts[key],
      } as AttestationForRequest;
    });
  }, [missedBreaksArray, attestationOptions, attestationTexts]);

  const isValid = attestations.every(
    (a) => a.option && (a.option !== AttestationOption.prevented || a.text)
  );

  const onIsDropdownOpenChange = (isOpen: boolean) => {
    setIsScrollLocked(isOpen);
  };

  return {
    attestationOptions,
    setAttestationOptions,
    attestationTexts,
    setAttestationTexts,
    missedBreaksArray,
    attestations,
    isValid,
    onIsDropdownOpenChange,
    isScrollLocked,
  };
};
